import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesignTimestamps, JackGirderConnection } from '@paragon/contract-library';

import { getRecursiveJackGuidsForComponent } from '../../library/utilities/getRecursiveJackGuidsForComponent';
import { RootState } from '..';
import { ComponentDesignTimestampsState } from '../slices/componentDesignTimestampsSlice';
import { SaveState } from '../slices/saveSlice';
import { getActiveComponentDesignGuid } from './getActiveComponentDesignSelector';
import { getJackGirderConnections } from './getJackGirderConnections';

function areWorkingChanges(
  designGuid: string,
  componentDesignTimestamps: ComponentDesignTimestampsState,
  save: SaveState,
  jackGirderConnections: JackGirderConnection[],
): boolean {
  const jackGuidsForComponent = getRecursiveJackGuidsForComponent(designGuid, jackGirderConnections);
  const relevantComponents = [designGuid].concat(jackGuidsForComponent);
  const anyRelevantComponentsHaveWorkingChanges = relevantComponents.some(
    (componentDesignGuid) =>
      timestampsRepresentWorkingChanges(componentDesignTimestamps[componentDesignGuid]) ||
      (save[componentDesignGuid] && save[componentDesignGuid] !== 'saved'),
  );
  const anyCarriedComponentsHaveBeenAnalyzedMoreRecently = jackGuidsForComponent.some((jackGuid) => {
    const carryingComponentTimestamps = componentDesignTimestamps[designGuid];
    const jackGuidTimestamps = componentDesignTimestamps[jackGuid];
    if (!carryingComponentTimestamps || !jackGuidTimestamps) {
      console.error('Timestamps for carried component not found.');
      return true;
    }

    if (!carryingComponentTimestamps.lastAnalyzed || !jackGuidTimestamps.lastAnalyzed) return true;
    const carryingComponentLastAnalyzed = new Date(carryingComponentTimestamps.lastAnalyzed);
    const jackLastAnalyzed = new Date(jackGuidTimestamps.lastAnalyzed);
    // FIXME This doesn't account for concurrent analysis or clock skew. For example, consider the following
    //  scenario:
    //   - Girder's loads are generated
    //   - Jack is analyzed
    //   - Girder is analyzed
    //  The timestamps would incorrectly suggest that the girder has no working changes. Storing a GUID or timestamp
    //  that represents the "version" of the jack that loads on the girder were generated with would fix this issue.
    return jackLastAnalyzed > carryingComponentLastAnalyzed;
  });
  return anyRelevantComponentsHaveWorkingChanges || anyCarriedComponentsHaveBeenAnalyzedMoreRecently;
}

export function createDoWorkingChangesExistOnComponentDesign(designGuid: string) {
  return createSelector(
    (state: RootState) => state.componentDesignTimestamps,
    (state: RootState) => state.save,
    getJackGirderConnections,

    (componentDesignTimestamps, save, jackGirderConnections) =>
      areWorkingChanges(designGuid, componentDesignTimestamps, save, jackGirderConnections),
  );
}

export const timestampsRepresentWorkingChanges = (timestamps: ComponentDesignTimestamps | undefined) => {
  if (timestamps) {
    if (!timestamps.lastAnalyzed) return true;
    return new Date(timestamps.lastUpdated) > new Date(timestamps.lastAnalyzed);
  }

  return false;
};

const createDoWorkingChangesExistForActiveComponentDesign = createSelector(
  getActiveComponentDesignGuid,
  (activeComponentDesignGuid) =>
    activeComponentDesignGuid != null
      ? createDoWorkingChangesExistOnComponentDesign(activeComponentDesignGuid)
      : () => undefined,
);

export function doWorkingChangesExistForActiveComponentDesign(state: RootState) {
  return createDoWorkingChangesExistForActiveComponentDesign(state)(state);
}

export const areWorkingChangesByComponentDesignGuidMap = createSelector(
  (state: RootState) => state.componentDesigns.data,
  (state: RootState) => state.componentDesignTimestamps,
  (state: RootState) => state.save,
  getJackGirderConnections,

  (componentDesigns, componentDesignTimestamps, save, jackGirderConnections) =>
    Object.fromEntries(
      Object.keys(componentDesigns).map((componentDesignGuid) => [
        componentDesignGuid,
        areWorkingChanges(componentDesignGuid, componentDesignTimestamps, save, jackGirderConnections),
      ]),
    ),
);
